import { MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { MprcFilterCountInfo } from "components/filter";
import RequestErrorUtils from "components/MprcRequestErrorReport";
import { FeedDataDto, FeedItemDto, FeedListQueryOrderBy, FeedType, GetListPermissionsResponse, PublicationSelectorDtoPaginatedList, PublicationSelectorOrder, PublicationSelectorType } from 'generated/CoreApi_v1';
import { usePageTitle } from 'hooks/usePageTitle';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { CardManager } from 'views/knowledge/components/card/CardManager';
import { RightPanelBreakpoints } from 'views/knowledge/feed/components/FeedContainer';
import { FeedCustomEventProps, publish, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { ListPublicationSelectorDrawer, PublicationSelectorDrawerResult } from "views/knowledge/publication/drawers/ListPublicationSelectorDrawer";
import { FeedListBox } from "views/knowledge/publication/pages/List/FeedListBox";
import { FeedListFilter, IFeedListFilterOptions } from "views/knowledge/publication/pages/List/FeedListFilter";
import { RightPanelContentPage } from '../common/RightPanelContentPage';

////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    openRightPanel: boolean;
    rightPanelBreakpoint: RightPanelBreakpoints;
    onCloseRightPanel?: () => void;
}

function isFiltered(filter?: IFeedListFilterOptions): boolean {
    return (filter?.searchText?.length || 0) > 0;
}

////////////////////////////////////////////////////////////////////////////////////////////////

export const ListPlaybackViewer: React.FC<IProps> = (props) => {

    const { publicationId, openRightPanel, rightPanelBreakpoint, onCloseRightPanel } = props;
    const location = useLocation();
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [publication, setPublication] = useState<FeedItemDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [addItemList, setAddItemList] = useState<PublicationSelectorDrawerResult | null>(null);
    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();
    const [count, setCount] = useState<number | undefined>(undefined);
    const permissions = useRef<GetListPermissionsResponse | null>(null);
    const filtered = useRef<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<IFeedListFilterOptions>({
        orderBy: FeedListQueryOrderBy.DisplayOrder,
        searchText: ""
    });

    usePageTitle(t("Feed.List.PageTitle"));

    const isPublic = publication?.listDefinition?.isPublic ?? false;

    ///
    const handleFilterChange = useCallback((filter: IFeedListFilterOptions) => {
        filtered.current = isFiltered(filter);
        setFilterOptions(filter);
    }, []);

    ///
    const handleRefreshData = (data?: FeedDataDto, resetFilter?: boolean) => {
        if (resetFilter) {
            setFilterOptions({
                orderBy: FeedListQueryOrderBy.DisplayOrder,
                searchText: ""
            });
        }
        setCount(data?.totalRows ?? 0);
    };

    ///
    const handleAdd = () => {
        setDialog(
            <ListPublicationSelectorDrawer
                title={t("Feed.Card.List.PublicationDrawerTitle")}
                createListExclusiveId={
                    isPublic && permissions.current?.canCreateExclusive == true
                        ? publicationId
                        : undefined}
                onSelect={(publication) => setAddItemList(publication)}
                onClose={() => setDialog(null)}
                onAddNewPost={() => {
                    publish("ListItemAdded", publicationId!);
                    setDialog(null);
                }}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<PublicationSelectorDtoPaginatedList>((resolve, reject) => {
                        api.v1.knowledgeSelectors.publications({
                            searchText,
                            pageNumber,
                            pageSize,
                            selectorType: PublicationSelectorType.AddListItem,
                            listPublicationId: publicationId ?? null,
                            onlyPostPublications: false,
                            orderBy: PublicationSelectorOrder.Title,
                            userId: null
                        }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ///
    const load = (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        Promise.all([
            api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead }),
            api.v1.knowledgePublication.getListPermissions({ publicationId })
        ])
            .then((results) => {
                setPublication(results[0].data);
                permissions.current = results[1].data;
            })
            .catch((error: any) => {
                setViewError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    ///
    const handlePublicationUpdate = (e: FeedCustomEventProps) => {
        if (e.detail == publicationId) {
            load(publicationId!);
        }
    };

    ///
    const handleAddItems = (e: FeedCustomEventProps) => {
        if (e.detail == publicationId) {
            handleAdd();
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        subscribe("ListAddItems", handleAddItems);
        subscribe("ListItemAdded", handlePublicationUpdate);
        subscribe("ListReload", handlePublicationUpdate);
        return () => {
            unsubscribe("ListAddItems", handleAddItems);
            unsubscribe("ListItemAdded", handlePublicationUpdate);
            unsubscribe("ListReload", handlePublicationUpdate);
        };
    }, []);

    ///
    useEffect(() => {
        if (publicationId != null) {
            setLoading(true);
            load(publicationId);
        }
    }, [publicationId]);

    ///
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.knowledgePublication.addListItem({
                    listPublicationId: publicationId!,
                    itemPublicationId: addItemList!.publicationId
                });

                if (result) {
                    publish("ListItemAdded", publicationId!);
                }
            }
            catch (e) {
                const errorMessage = RequestErrorUtils.findValidationMessage(e, "itemPublicationId", "ListPublicationItemAlreadyExists");
                if (errorMessage) enqueueSnackbar(errorMessage, { variant: "error" });
                else setViewError(e);
            }
            finally {
                setAddItemList(null);
            }
        }

        if (addItemList) postData();

    }, [addItemList]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderCardHeader = useCallback(() => {
        return (
            <>
                {
                    publication ?
                        <>
                            <MprcContainer>
                                <CardManager
                                    feedType={FeedType.ContentPage}
                                    pub={publication}
                                    onNeedResetRowHeight={() => { }}
                                    onReload={() => load(publicationId!)}
                                    onFeedItemChanged={() => { load(publicationId!); }}
                                />
                                <FeedListFilter
                                    filterOptions={filterOptions}
                                    listType={publication.listDefinition?.listType}
                                    onAdd={
                                        permissions.current?.canAddItems
                                            ? handleAdd
                                            : undefined
                                    }
                                    onFilterChange={handleFilterChange}
                                />
                            </MprcContainer>

                            {
                                count != undefined &&
                                <MprcContainer>
                                    <MprcFilterCountInfo
                                        variant={filtered.current ? "bodyNotice" : (count ?? 0) == 0 ? "captionHeader" : "caption"}
                                        text={t("Feed.List.FilterInfoMessage", { count: count ?? 0 })}
                                    />
                                </MprcContainer>
                            }
                        </>
                        : null
                }
            </>
        );
    }, [publication, count]);

    ///
    return (
        <div>

            {dialog}

            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : <FeedListBox
                            publication={publication!}
                            feedType={FeedType.ListViewer}
                            cardHeader={RenderCardHeader()}
                            rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                            filter={filterOptions}
                            count={count}
                            permissions={permissions.current}
                            openRightPanel={openRightPanel}
                            marginRightPanel={0}
                            onRefreshData={handleRefreshData}
                            onCloseRightPanel={onCloseRightPanel}
                            rightPanelBreakpoint={rightPanelBreakpoint}
                        />
            }
        </div>
    );
};
